import React, { useState, useEffect, useRef } from 'react';
import { t } from '../i18n';
import BlueTickBadge from '../components/BlueTickBadge';
import { useParams } from 'react-router-dom';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

function Profile({ user, onAvatarUpdate }) {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [hasBlueTick, setHasBlueTick] = useState(false);
  const [btLoading, setBtLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [connStatus, setConnStatus] = useState('none');
  const [connId, setConnId] = useState(null);
  const [connActing, setConnActing] = useState(false);
  const fileInputRef = useRef(null);
  const isOwnProfile = user && user.username === username;
  const navigate = typeof window !== 'undefined' ? (path) => window.location.href = path : null;

  useEffect(() => {
    Promise.all([
      fetch(`/api/profile/${username}/`).then(r => r.json()),
      fetch(`/api/posts/`).then(r => r.json())
    ]).then(([prof, allPosts]) => {
      setProfile(prof);
      const filtered = Array.isArray(allPosts) ? allPosts.filter(p => p.author.username === username) : [];
      setPosts(filtered);
      setLoading(false);
    });
  }, [username]);

  const handleAvatarClick = () => {
    if (isOwnProfile && fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const response = await fetch('/api/profile/avatar/', {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}` },
        body: formData
      });
      const data = await response.json();
      if (data.status === 'success') {
        const newAvatar = data.avatar || URL.createObjectURL(file);
        setProfile(prev => ({ ...prev, avatar: newAvatar }));
        if (onAvatarUpdate) onAvatarUpdate(newAvatar);
        alert('␅ Profile picture updated!');
      }
    } catch (err) {
      alert('Upload error: ' + err.message);
    }
    setUploading(false);
  };

  useEffect(() => {
    if (user && user.username !== username) {
      fetch(`/api/connections/status/${username}/`, {
        headers: { 'Authorization': `Token ${user.token}` }
      }).then(r => r.json()).then(d => { setConnStatus(d.status); setConnId(d.id); });
    }
  }, [username, user]);

  useEffect(() => {
    if (isOwnProfile && user) {
      fetch('/api/blue-tick/', {
        headers: { 'Authorization': `Token ${user.token}` }
      }).then((r) => r.json()).then((d) => setHasBlueTick(!!d.has_blue_tick)).catch(() => {});
    }
  }, [isOwnProfile, user]);

  const handleConnect = async () => {
    if (!user) { alert(t('pf_login_first')); return; }
    setConnActing(true);
    try {
      if (connStatus === 'none') {
        await fetch(`/api/connections/send/${username}/`, {
          method: 'POST', headers: { 'Authorization': `Token ${user.token}` }
        });
        setConnStatus('pending');
      } else if (connStatus === 'accepted') {
        if (window.confirm(`Disconnect from @${username}?`)) {
          await fetch(`/api/connections/disconnect/${username}/`, {
            method: 'DELETE', headers: { 'Authorization': `Token ${user.token}` }
          });
          setConnStatus('none');
          setConnId(null);
        }
      }
    } catch (err) { console.error(err); }
    setConnActing(false);
  };

  if (loading) return <div style={styles.loading}>{t('pf_loading')}</div>;
  if (!profile || profile.error) return <div style={styles.loading}>{t('pf_not_found')}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.profileCard}>
        <div
          style={{ ...styles.avatarContainer, cursor: isOwnProfile ? 'pointer' : 'default' }}
          onClick={handleAvatarClick}
        >
          {profile.avatar ? (
            <img src={profile.avatar} alt={username} style={styles.avatarImg} />
          ) : (
            <div style={styles.avatar}>{username.charAt(0).toUpperCase()}</div>
          )}
          {isOwnProfile && (
            <div style={styles.editBadge}>{uploading ? '...' : '📷'}</div>
          )}
        </div>

        {isOwnProfile && (
          <input type="file" accept="image/*" ref={fileInputRef} onChange={handleAvatarChange} style={{ display: 'none' }} />
        )}

        <h2 style={styles.username}>@{username} {hasBlueTick && <BlueTickBadge size={20} />}</h2>
        {isOwnProfile && (
          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.65)', letterSpacing: '0.5px', marginBottom: '10px', textTransform: 'uppercase', fontWeight: 600 }}>
            Your Profile
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '10px' }}>
          {isOwnProfile && pendingCount > 0 && (
            <a href="/connections" style={{ backgroundColor: 'rgba(239,68,68,0.9)', color: 'white', fontSize: '12px', padding: '5px 12px', borderRadius: '16px', fontWeight: 600, textDecoration: 'none' }}>
              🔔 {pendingCount} Pending
            </a>
          )}
          {isOwnProfile && (
            <a href="/my-subscriptions" style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', fontSize: '12px', padding: '5px 12px', borderRadius: '16px', fontWeight: 600, textDecoration: 'none' }}>
              My Subscriptions
            </a>
          )}
        </div>
        {isOwnProfile && !hasBlueTick && (
          <button onClick={async () => {
            if (!window.Pi) { alert(t('pf_pi_browser')); return; }
            try {
              await window.Pi.authenticate(['username', 'payments'], () => {});
            } catch (e) {
              alert(t('pf_auth_failed'));
              return;
            }
            window.Pi.createPayment(
              {
                amount: 1,
                memo: 'PenFlow Blue Tick - 30 days',
                metadata: { type: 'blue_tick' },
              },
              {
                onReadyForServerApproval: (paymentId) => {
                  fetch('/api/payments/approve/', {
                    method: 'POST', headers: { 'Authorization': 'Token ' + user.token, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ paymentId, amount: 1 })
                  });
                },
                onReadyForServerCompletion: (paymentId, txid) => {
                  fetch('/api/payments/complete/', {
                    method: 'POST', headers: { 'Authorization': 'Token ' + user.token, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ paymentId, txid, postId: null })
                  }).then(() => {
                    fetch('/api/blue-tick/activate/', {
                      method: 'POST', headers: { 'Authorization': 'Token ' + user.token, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ paymentId, txid, amount: 1 })
                    }).then(() => { setHasBlueTick(true); alert(t('pf_bluetick_done')); })
                      .catch(() => alert('Payment reached Pi, but activation failed - please contact support with your payment ID.'));
                  }).catch(() => alert(t('pf_payment_failed')));
                },
                onCancel: (paymentId) => { console.log('cancelled', paymentId); },
                onError: (error, payment) => { console.error(error, payment); }
              }
            );
          }} style={{ backgroundColor: '#1d9bf0', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', marginTop: '8px' }}>
            ✓ Get Blue Tick (1π/month)
          </button>
        )}
        {isOwnProfile && hasBlueTick && (
          <div style={{ backgroundColor: '#e7f3ff', color: '#1d9bf0', padding: '6px 14px', borderRadius: '20px', fontSize: '12px', marginTop: '6px', fontWeight: 'bold' }}>
            ✓ Blue Tick Active
          </div>
        )}

        <div style={styles.badgeRow}>
          {profile.is_top_earner && <span style={styles.topBadge}>🏆 Top 10 Earner</span>}
          {profile.is_top_writer && <span style={styles.topBadge}>✍️ Top 10 Contributor</span>}
        </div>

        <div style={styles.joinedText}>📅 Joined since {profile.date_joined}</div>
        {user && user.username !== username && (
          <div style={{ marginTop: '12px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleConnect}
              disabled={connActing || connStatus === 'pending'}
              style={{
                backgroundColor: connStatus === 'accepted' ? '#ff4444' : connStatus === 'pending' ? '#0d9488' : '#0F6E56',
                color: connStatus === 'none' ? '#000' : 'white',
                border: 'none', padding: '10px 24px', borderRadius: '20px',
                cursor: connStatus === 'pending' ? 'default' : 'pointer',
                fontWeight: 'bold', fontSize: '15px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              {connActing ? '...' : connStatus === 'accepted' ? '❌ Disconnect' : connStatus === 'pending' ? '⏳ Pending' : '🤝 Connect'}
            </button>
            {connStatus === 'accepted' && (
              <a href={`/chat/${username}`} style={{ backgroundColor: '#0F6E56', color: 'white', padding: '10px 24px', borderRadius: '20px', fontWeight: 'bold', fontSize: '15px', textDecoration: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                💬 Message
              </a>
            )}
          </div>
        )}

        <div style={styles.statsRow}>
          <div style={styles.stat}>
            <span style={styles.statNum}>{profile.post_count}</span>
            <span style={styles.statLabel}>{t('pf_posts')}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statNum}>{parseFloat(profile.total_earned).toFixed(2)}</span>
            <span style={styles.statLabel}>π Earned</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statNum}>{profile.total_votes}</span>
            <span style={styles.statLabel}>{t('pf_votes')}</span>
          </div>
          <a href="/connections" style={{ ...styles.stat, textDecoration: 'none' }}>
            <span style={styles.statNum}>{profile.connection_count || 0}</span>
            <span style={styles.statLabel}>{t('pf_connections')}</span>
          </a>
        </div>
      </div>

      <h2 style={styles.postsHeading}>📝 Posts by @{username}</h2>

      {posts.length === 0 ? (
        <div style={styles.empty}><p>{t('pf_no_posts')}</p></div>
      ) : (
        [...posts].sort((a,b) => new Date(b.created_at) - new Date(a.created_at)).map(post => (
          <div key={post.id} style={styles.card}>
            <div style={styles.postMeta}>
              <span style={styles.postDate}>📅 {formatDate(post.created_at)}</span>
            </div>
            <h3 style={styles.postTitle}>
              <a href={`/post/${post.id}`} style={styles.link}>{post.title}</a>
            </h3>
            <p style={styles.postBody}>{post.body.substring(0, 150)}...</p>
            <div style={styles.postFooter}>
              <span style={styles.reward}>💰 {post.pi_reward_pool} Pi</span>
              <span style={styles.votes}>👍 {post.total_votes} votes</span>
              <span style={styles.comments}>💬 {post.comments.length} comments</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '800px', margin: '0 auto', padding: '20px' },
  profileCard: { backgroundColor: '#0F6E56', color: 'white', borderRadius: '10px', padding: '30px', textAlign: 'center', marginBottom: '30px' },
  avatarContainer: { position: 'relative', width: '90px', height: '90px', margin: '0 auto 15px' },
  avatarImg: { width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #0F6E56' },
  avatar: { width: '90px', height: '90px', borderRadius: '50%', backgroundColor: '#0F6E56', color: '#fff', fontSize: '40px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  editBadge: { position: 'absolute', bottom: '-5px', right: '-5px', backgroundColor: '#0F6E56', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '2px solid white' },
  username: { fontSize: '24px', marginBottom: '10px' },
  badgeRow: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '10px' },
  youBadge: { backgroundColor: '#0F6E56', color: '#000', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' },
  topBadge: { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.5)' },
  joinedText: { fontSize: '13px', color: '#ddd', marginBottom: '15px' },
  statsRow: { display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '15px' },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statNum: { fontSize: '24px', fontWeight: 'bold', color: 'white' },
  statLabel: { fontSize: '12px', color: '#ddd' },
  postsHeading: { color: '#333', marginBottom: '20px' },
  postMeta: { marginBottom: '6px' },
  postDate: { color: '#999', fontSize: '12px' },
  card: { backgroundColor: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '15px' },
  postTitle: { marginBottom: '10px' },
  link: { color: '#0F6E56', textDecoration: 'none' },
  postBody: { color: '#666', lineHeight: '1.6', marginBottom: '15px' },
  postFooter: { display: 'flex', gap: '20px' },
  reward: { color: '#0F6E56', fontWeight: 'bold' },
  votes: { color: '#0F6E56' },
  comments: { color: '#666' },
  empty: { textAlign: 'center', padding: '50px', backgroundColor: 'white', borderRadius: '10px' },
  loading: { textAlign: 'center', padding: '50px', fontSize: '20px' },
};

export default Profile;
